import styled from "@emotion/styled";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import { FlexContent } from "components/FlexContent/FlexContent";
import { ReactComponent as AddedIcon } from "icons/changeHistory/added.svg";
import { ReactComponent as RemovedIcon } from "icons/changeHistory/removed.svg";
import { ReactComponent as UpdatedIcon } from "icons/changeHistory/updated.svg";
import { useState } from "react";
import { getComponentForFieldValue } from "./cells/fieldValueRenderers";
import {
  CreativeChangeDetail,
  holdsEmbeddedMarkup,
} from "./creativeCustomCases";
import { isAspectRatioPath } from "./creativeSnapshotFields";
import { ChangeHistoryAction } from "./types";
import { useSnapshotResolvedChange } from "./useSnapshotResolvedChange";
import { getFieldAccordionCountFormatter } from "./utils";

const ChangeTypeGroupContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GraphColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20px;
  position: relative;
`;

const getActionColor = (action: ChangeHistoryAction) => {
  switch (action) {
    case "updated":
      return "#05ACC1";
    case "added":
      return "#4CAF50";
    case "removed":
      return "#B3261E";
    default:
      return "#000";
  }
};

const ACTION_ICON_MAP = {
  updated: UpdatedIcon,
  added: AddedIcon,
  removed: RemovedIcon,
};

const GraphLine = styled.div<{ action: ChangeHistoryAction }>`
  width: 2px;
  flex: 1;
  background-color: ${({ action }) => getActionColor(action)};
  margin-top: 4px;
`;

const ChangeContent = styled.div`
  flex: 1;
`;

const ActionText = styled.span<{ action: ChangeHistoryAction }>`
  color: ${({ action }) => getActionColor(action)};
  font-weight: 500;
`;

const StrikethroughText = styled.span`
  text-decoration: line-through;
  color: ${COLORS_TO_BE_REVIEWED.anotherLightGray};
`;

const DetailRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 2px;
`;

const DetailLabel = styled.span`
  color: ${COLORS_TO_BE_REVIEWED.anotherDarkGrey};
  min-width: 140px;
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  flex: 1;
  min-width: 0;
`;

const ChangeValues = styled.div`
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

const ExpandButton = styled.div<{ expanded: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${COLORS_TO_BE_REVIEWED.anotherDarkGrey};
  transform: ${({ expanded }) =>
    expanded ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.2s;
`;

type ChangeTypeGroupProps = {
  action: ChangeHistoryAction;
  fieldName: string;
  fieldPath: string;
  oldValue?: string;
  newValue?: string;
  arrayLength?: number;
  details?: CreativeChangeDetail[];
  oldSnapshotId?: string;
  newSnapshotId?: string;
};

const DetailList = ({ items }: { items: CreativeChangeDetail[] }) => (
  <>
    {items.map((item) => (
      <DetailRow key={item.label}>
        <DetailLabel>{item.label}</DetailLabel>
        <DetailValue>
          {item.oldValue && (
            <>
              <StrikethroughText>{item.oldValue}</StrikethroughText>
              {" \u2192 "}
            </>
          )}
          {item.newValue}
        </DetailValue>
      </DetailRow>
    ))}
  </>
);

function getAccordionInfo({
  arrayLength,
  oldValue,
  newValue,
  fieldName,
  action,
  fieldPath,
  details,
}: {
  arrayLength?: number;
  oldValue?: string;
  newValue?: string;
  fieldName: string;
  action: ChangeHistoryAction;
  fieldPath: string;
  details?: CreativeChangeDetail[];
}): { hasAccordion: boolean; accordionHeader: string } {
  if (details?.length) {
    return { hasAccordion: true, accordionHeader: fieldName };
  }

  if (holdsEmbeddedMarkup(fieldPath)) {
    return { hasAccordion: false, accordionHeader: fieldName };
  }

  if (arrayLength && arrayLength > 1) {
    const fieldFormatter = getFieldAccordionCountFormatter(fieldPath);
    const count: number | string = fieldFormatter
      ? fieldFormatter.formatter(arrayLength)
      : arrayLength;
    const unit = fieldFormatter ? fieldFormatter.unit : "items";

    return {
      hasAccordion: true,
      accordionHeader: `${count} ${unit} ${
        action === "added" ? "to" : "from"
      } ${fieldName}`,
    };
  }

  const totalLength = (oldValue?.length || 0) + (newValue?.length || 0);
  const isLongValue = totalLength > 100;
  return { hasAccordion: isLongValue, accordionHeader: fieldName };
}

export const ChangeTypeGroup = ({
  action,
  fieldName,
  fieldPath,
  oldValue,
  newValue,
  arrayLength,
  details,
  oldSnapshotId,
  newSnapshotId,
}: ChangeTypeGroupProps) => {
  const ActionIcon = ACTION_ICON_MAP[action];
  const [expanded, setExpanded] = useState(false);

  const { fieldName: resolvedFieldName, snapshotDetails } =
    useSnapshotResolvedChange({
      fieldName,
      fieldPath,
      action,
      oldSnapshotId,
      newSnapshotId,
    });

  const accordion = getAccordionInfo({
    newValue,
    oldValue,
    fieldName: resolvedFieldName,
    arrayLength,
    action,
    fieldPath,
    details,
  });

  // the chevron is decided from the ids so it does not appear once the fetch lands,
  // and stays off when there is nothing to derive the details from
  const derivesFromSnapshot =
    isAspectRatioPath(fieldPath) && !!oldSnapshotId && !!newSnapshotId;
  const hasAccordion = accordion.hasAccordion || derivesFromSnapshot;
  const { accordionHeader } = accordion;
  const valueVisible =
    !!newValue &&
    !holdsEmbeddedMarkup(fieldPath) &&
    (!hasAccordion || expanded || derivesFromSnapshot);
  const detailsVisible = expanded && snapshotDetails.length > 0;

  const renderValue = (value?: string) => {
    if (!value) return null;
    return getComponentForFieldValue(fieldPath, value);
  };

  return (
    <ChangeTypeGroupContainer>
      <GraphColumn>
        <ActionIcon />
        <GraphLine action={action} />
      </GraphColumn>
      <ChangeContent>
        <FlexContent
          align="center"
          gap="8px">
          <div>
            <ActionText action={action}>
              {action.charAt(0).toUpperCase() + action.slice(1)}
            </ActionText>{" "}
            {hasAccordion ? accordionHeader : resolvedFieldName}
          </div>
          {hasAccordion && (
            <ExpandButton
              expanded={expanded}
              onClick={() => setExpanded(!expanded)}>
              <KeyboardArrowDownIcon fontSize="small" />
            </ExpandButton>
          )}
        </FlexContent>
        <ChangeValues>
          {details?.length ? (
            expanded && <DetailList items={details} />
          ) : (
            <>
              {valueVisible ? (
                <>
                  {oldValue && (
                    <>
                      <StrikethroughText>
                        {renderValue(oldValue)}
                      </StrikethroughText>
                      {" \u2192 "}
                    </>
                  )}
                  {renderValue(newValue)}
                </>
              ) : null}
              {detailsVisible && <DetailList items={snapshotDetails} />}
              {!valueVisible && !detailsVisible && "\u00A0"}
            </>
          )}
        </ChangeValues>
      </ChangeContent>
    </ChangeTypeGroupContainer>
  );
};
